import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { ImageField } from "@/components/ImageField";

const inputCls =
  "w-full rounded-lg border-2 border-[#F5F2EB] bg-[#141414] px-4 py-2.5 text-sm font-mono text-[#F5F2EB] placeholder-[#F5F2EB]/40 transition-shadow focus:shadow-[3px_3px_0_#F5F2EB] focus:outline-none";

const emptyForm = { title: "", category: "Outerwear", size: "M", condition: "Good", price: "", status: "available", image: "", description: "" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sales, setSales] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [banner, setBanner] = useState({ enabled: false, text: "" });
  const [bannerSaving, setBannerSaving] = useState(false);

  const loadAll = useCallback(async () => {
    const [p, i, m, sa, b, al] = await Promise.all([
      api.get("/products"),
      api.get("/inquiries"),
      api.get("/contact"),
      api.get("/payments"),
      api.get("/settings/drop-banner"),
      api.get("/alerts"),
    ]);
    setProducts(p.data);
    setInquiries(i.data);
    setMessages(m.data);
    setSales(sa.data);
    setBanner(b.data);
    setAlerts(al.data);
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadAll().catch(() => toast.error("Failed to load data"));
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate, loadAll]);

  const logout = () => {
    localStorage.removeItem("jfly_token");
    navigate("/admin/login");
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Piece updated");
      } else {
        await api.post("/products", payload);
        toast.success("Piece added to the rack");
      }
      setForm(emptyForm);
      setEditingId(null);
      const p = await api.get("/products");
      setProducts(p.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      category: p.category,
      size: p.size,
      condition: p.condition,
      price: String(p.price),
      status: p.status,
      image: p.image,
      description: p.description || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Piece removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (id, status) => {
    try {
      const r = await api.put(`/products/${id}`, { status });
      setProducts((prev) => prev.map((p) => (p.id === id ? r.data : p)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleInquiry = async (inq) => {
    const next = inq.status === "new" ? "replied" : "new";
    try {
      const r = await api.patch(`/inquiries/${inq.id}`, { status: next });
      setInquiries((prev) => prev.map((x) => (x.id === inq.id ? r.data : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveBanner = async () => {
    setBannerSaving(true);
    try {
      const { data } = await api.put("/settings/drop-banner", banner);
      setBanner(data);
      toast.success(data.enabled ? "Drop banner is live" : "Drop banner hidden");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBannerSaving(false);
    }
  };

  if (!user) {
    return (
      <div data-testid="admin-loading" className="flex min-h-screen items-center justify-center text-xs font-mono uppercase tracking-[0.3em] text-[#F5F2EB]/50">
        Checking session…
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen px-6 py-10 md:px-12 lg:px-20">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b-2 border-[#F5F2EB] pb-6">
        <div>
          <p className="text-[10px] font-mono font-medium uppercase tracking-[0.3em] text-[#F5F2EB]/50">JFLY — Back Office</p>
          <h1 className="mt-1 font-serif text-3xl font-black uppercase tracking-tight">Admin</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/"
            data-testid="admin-view-site-link"
            className="rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-4 py-2 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-[#F5F2EB] transition-all hover:bg-[#F5F2EB] hover:text-black"
          >
            View Site
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="rounded-full border-2 border-[#F5F2EB] bg-[#FF3B1F] px-4 py-2 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-white shadow-[3px_3px_0_#F5F2EB] transition-transform hover:-translate-y-0.5"
          >
            Log Out
          </button>
        </div>
      </header>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
        {[
          ["products", `Pieces (${products.length})`],
          ["inquiries", `Inquiries (${inquiries.filter((i) => i.status === "new").length} new)`],
          ["messages", `Messages (${messages.length})`],
          ["sales", `Sales (${sales.filter((x) => x.payment_status === "paid").length})`],
          ["alerts", `Alerts (${alerts.filter((a) => !a.notified).length} waiting)`],
          ["banner", "Drop Banner"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`rounded-full border-2 border-[#F5F2EB] px-4 py-2 text-[10px] font-mono font-medium uppercase tracking-[0.2em] transition-colors ${
              tab === key ? "bg-[#F5F2EB] text-black" : "bg-[#141414] text-[#F5F2EB]/60 hover:text-[#F5F2EB]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="mt-8 grid gap-10 lg:grid-cols-[380px_1fr]">
          <form onSubmit={submitProduct} data-testid="product-form" className="h-fit space-y-3 rounded-2xl border-2 border-[#F5F2EB] bg-[#141414] p-6 shadow-[6px_6px_0_#F5F2EB]">
            <p className="text-[10px] font-mono font-semibold uppercase tracking-[0.3em] text-[#F5F2EB]/50">
              {editingId ? "Edit piece" : "Add a piece"}
            </p>
            <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="product-form-title" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-form-category" className={inputCls} />
              <input placeholder="Size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} data-testid="product-form-size" className={inputCls} />
              <input placeholder="Condition" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} data-testid="product-form-condition" className={inputCls} />
              <input required type="number" min="0" step="0.01" placeholder="Price ($)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-form-price" className={inputCls} />
            </div>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="product-form-status" className={inputCls}>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="sold">Sold</option>
            </select>
            <ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
            <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-form-description" className={inputCls} />
            <div className="flex gap-3">
              <button
                disabled={saving}
                data-testid="product-form-submit"
                className="flex-1 rounded-full border-2 border-[#F5F2EB] bg-[#FF3B1F] px-4 py-2.5 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-white shadow-[3px_3px_0_#F5F2EB] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
              >
                {saving ? "Saving…" : editingId ? "Save Changes" : "Add Piece"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setForm(emptyForm);
                  }}
                  data-testid="product-form-cancel"
                  className="rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-4 py-2.5 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-[#F5F2EB]/60"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="space-y-3" data-testid="admin-product-list">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-3">
                <img src={p.image} alt={p.title} className="h-14 w-14 rounded-lg border-2 border-[#F5F2EB] object-cover" />
                <div className="min-w-[160px] flex-1">
                  <p className="font-serif text-sm font-extrabold uppercase">{p.title}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#F5F2EB]/50">
                    {p.category} — {p.size} — {p.condition}
                  </p>
                </div>
                <p className="font-mono text-sm font-semibold text-[#FF3B1F]">${p.price}</p>
                <select
                  value={p.status}
                  onChange={(e) => setStatus(p.id, e.target.value)}
                  data-testid={`admin-product-status-${p.id}`}
                  className="rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-2 py-1.5 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-[#F5F2EB]"
                >
                  <option value="available">Available</option>
                  <option value="reserved">Reserved</option>
                  <option value="sold">Sold</option>
                </select>
                <button
                  onClick={() => startEdit(p)}
                  data-testid={`admin-product-edit-${p.id}`}
                  className="rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-3 py-1.5 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-[#F5F2EB]/70 transition-all hover:bg-[#F5F2EB] hover:text-black"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`admin-product-delete-${p.id}`}
                  className="rounded-full border-2 border-[#FF3B1F] bg-[#141414] px-3 py-1.5 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-[#FF3B1F] transition-all hover:bg-[#FF3B1F] hover:text-white"
                >
                  Delete
                </button>
              </div>
            ))}
            {products.length === 0 && (
              <p className="rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-8 text-center text-xs font-mono text-[#F5F2EB]/50">No pieces yet — add your first one.</p>
            )}
          </div>
        </div>
      )}

      {tab === "inquiries" && (
        <div className="mt-8 space-y-3" data-testid="admin-inquiry-list">
          {inquiries.map((inq) => (
            <div key={inq.id} data-testid={`admin-inquiry-row-${inq.id}`} className="rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-sm font-extrabold uppercase">{inq.product_title}</p>
                  <p className="mt-1 text-xs font-mono text-[#F5F2EB]/70">
                    {inq.name} —{" "}
                    <a href={`mailto:${inq.email}`} className="font-semibold text-[#FF3B1F]">
                      {inq.email}
                    </a>
                    {inq.offer ? ` — offers $${inq.offer}` : ""}
                  </p>
                  {inq.address && <p className="mt-1 text-xs font-mono text-[#F5F2EB]/50">Ship to: {inq.address}</p>}
                  {inq.message && <p className="mt-2 text-sm text-[#F5F2EB]/70">“{inq.message}”</p>}
                </div>
                <button
                  onClick={() => toggleInquiry(inq)}
                  data-testid={`admin-inquiry-toggle-${inq.id}`}
                  className={`rounded-full border-2 border-[#F5F2EB] px-3 py-1.5 text-[10px] font-mono font-medium uppercase tracking-[0.2em] transition-colors ${
                    inq.status === "new" ? "bg-[#FF3B1F] text-white" : "bg-[#141414] text-[#F5F2EB]/50"
                  }`}
                >
                  {inq.status === "new" ? "New — mark replied" : "Replied"}
                </button>
              </div>
            </div>
          ))}
          {inquiries.length === 0 && (
            <p className="rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-8 text-center text-xs font-mono text-[#F5F2EB]/50">No inquiries yet.</p>
          )}
        </div>
      )}

      {tab === "messages" && (
        <div className="mt-8 space-y-3" data-testid="admin-message-list">
          {messages.map((m) => (
            <div key={m.id} data-testid={`admin-message-row-${m.id}`} className="rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-5">
              <p className="text-xs font-mono text-[#F5F2EB]/70">
                {m.name} —{" "}
                <a href={`mailto:${m.email}`} className="font-semibold text-[#FF3B1F]">
                  {m.email}
                </a>
              </p>
              <p className="mt-2 text-sm text-[#F5F2EB]/80">{m.message}</p>
            </div>
          ))}
          {messages.length === 0 && (
            <p className="rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-8 text-center text-xs font-mono text-[#F5F2EB]/50">No messages yet.</p>
          )}
        </div>
      )}

      {tab === "sales" && (
        <div className="mt-8 space-y-3" data-testid="admin-sales-list">
          {sales.map((s) => (
            <div key={s.session_id} data-testid={`admin-sale-row-${s.session_id}`} className="flex flex-wrap items-center gap-4 rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-4">
              <div className="min-w-[160px] flex-1">
                <p className="font-serif text-sm font-extrabold uppercase">{s.product_title || "Unknown piece"}</p>
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#F5F2EB]/50">{(s.created_at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
              <p className="font-mono text-sm font-semibold text-[#F5F2EB]">${s.amount}</p>
              <span
                data-testid={`admin-sale-status-${s.session_id}`}
                className={`rounded-full border-2 border-[#F5F2EB] px-3 py-1 text-[10px] font-mono font-semibold uppercase tracking-[0.2em] ${
                  s.payment_status === "paid" ? "bg-emerald-500 text-white" : "bg-[#141414] text-[#F5F2EB]/50"
                }`}
              >
                {s.payment_status}
              </span>
            </div>
          ))}
          {sales.length === 0 && (
            <p className="rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-8 text-center text-xs font-mono text-[#F5F2EB]/50">No sales yet.</p>
          )}
        </div>
      )}

      {tab === "alerts" && (
        <div className="mt-8 space-y-3" data-testid="admin-alerts-list">
          {alerts.map((a) => (
            <div key={a.id} data-testid={`admin-alert-row-${a.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-4">
              <div className="min-w-[180px] flex-1">
                <a href={`mailto:${a.email}`} className="font-mono text-sm font-semibold text-[#FF3B1F]">{a.email}</a>
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#F5F2EB]/50">
                  wants {a.category} — missed: {a.product_title}
                </p>
              </div>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#F5F2EB]/50">{(a.created_at || "").slice(0, 10)}</p>
              <span
                data-testid={`admin-alert-status-${a.id}`}
                className={`rounded-full border-2 border-[#F5F2EB] px-3 py-1 text-[10px] font-mono font-semibold uppercase tracking-[0.2em] ${
                  a.notified ? "bg-emerald-500 text-white" : "bg-[#141414] text-[#F5F2EB]/50"
                }`}
              >
                {a.notified ? "Notified" : "Waiting"}
              </span>
            </div>
          ))}
          {alerts.length === 0 && (
            <p className="rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-8 text-center text-xs font-mono text-[#F5F2EB]/50">
              No one on the waitlist yet — emails left on sold pieces appear here.
            </p>
          )}
        </div>
      )}

      {tab === "banner" && (
        <div className="mt-8 max-w-lg" data-testid="admin-banner-settings">
          <div className="space-y-4 rounded-2xl border-2 border-[#F5F2EB] bg-[#141414] p-6 shadow-[6px_6px_0_#F5F2EB]">
            <p className="text-[10px] font-mono font-semibold uppercase tracking-[0.3em] text-[#F5F2EB]/50">TikTok drop teaser</p>
            <label className="flex items-center gap-3 text-sm font-mono">
              <input
                type="checkbox"
                checked={banner.enabled}
                onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })}
                data-testid="banner-enabled-toggle"
                className="h-5 w-5 accent-[#FF3B1F]"
              />
              Show the drop banner on the site
            </label>
            <input
              value={banner.text}
              onChange={(e) => setBanner({ ...banner, text: e.target.value })}
              placeholder="New drop is live — as seen on TikTok @jitclips2"
              data-testid="banner-text-input"
              className={inputCls}
            />
            <button
              onClick={saveBanner}
              disabled={bannerSaving}
              data-testid="banner-save-button"
              className="rounded-full border-2 border-[#F5F2EB] bg-[#FF3B1F] px-6 py-2.5 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-white shadow-[3px_3px_0_#F5F2EB] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
            >
              {bannerSaving ? "Saving…" : "Save Banner"}
            </button>
            {banner.enabled && (
              <div data-testid="banner-preview" className="rounded-lg border-2 border-[#F5F2EB] bg-[#FF3B1F] px-4 py-2 text-center text-[10px] font-mono font-semibold uppercase tracking-[0.2em] text-white">
                {banner.text || "New drop is live — as seen on TikTok @jitclips2"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
